/**
 * One-time dex resync-UP (server restore from the surviving local copy).
 *
 * WHY THIS EXISTS
 * ---------------
 * The retired clean-slate wipe (see dexWipeMigration.ts) deleted server
 * `dex_records` on already-migrated devices via the audit-backfill
 * `DELETE /api/dex/state`, while those devices kept their FULL local dex. The
 * result: a complete dex on-device, an empty one on the server (and so on the
 * admin dashboard and every other device). Retiring the wipe stops further
 * damage; this task repairs the damage already done.
 *
 * WHAT IT DOES
 * ------------
 * On the first launch of a build carrying a newer `DEX_RESYNC_UP_VERSION`, once
 * the user is authenticated, it re-uploads the entire local dex to the server
 * (PUT /api/dex/state) so the backup matches the device again.
 *
 * WHY IT IS SAFE FOR EVERYONE (not just wiped users)
 * --------------------------------------------------
 *   - the server PUT is an UPSERT, never a delete, so it can only add/refresh
 *     rows — a device whose server copy is already correct just re-writes the
 *     same data (an effective no-op), and a device with an empty local dex
 *     enqueues nothing;
 *   - it runs at most once per device (a localStorage version marker), off the
 *     paint-critical path (queueMicrotask), and never blocks launch;
 *   - if it cannot complete (signed out, or the endpoint 404s on a partial
 *     rollout) it does NOT stamp the marker, so it simply retries next launch.
 */

import { resyncLocalDexToServer } from "./dexStateStore";

/** localStorage key holding the last resync-up version this device completed. */
export const DEX_RESYNC_UP_KEY = "nexus.dexResyncUpVersion";

/**
 * Current resync-up version shipped by this build. Bump to force a fresh
 * one-shot re-upload on every device (e.g. if a future incident empties the
 * server dex again). Ships version 1 to heal the audit-backfill wipe.
 *
 * v2: forces a fresh full re-upload after a server-side dex reset left devices
 * with a complete LOCAL dex but an empty/partial server copy. The device marker
 * from v1 was already stamped, so the normal per-change sync never re-pushed the
 * backlog; bumping the version re-runs the one-shot upsert on next launch.
 */
export const DEX_RESYNC_UP_VERSION = 2;

/** Read the stored resync-up version (0 if absent/unparseable). Total. */
function readResyncUpVersion(): number {
  if (typeof localStorage === "undefined") return 0;
  try {
    const n = Number.parseInt(localStorage.getItem(DEX_RESYNC_UP_KEY) ?? "", 10);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

/** Stamp the current resync-up version so the task runs at most once. Total. */
function writeResyncUpVersion(version: number): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(DEX_RESYNC_UP_KEY, String(version));
  } catch {
    /* quota — worst case the resync re-runs next launch; the PUT is an
       idempotent upsert, so a repeat is harmless */
  }
}

/** True when this device still owes a resync-up for the current version. */
export function isDexResyncUpPending(): boolean {
  return readResyncUpVersion() < DEX_RESYNC_UP_VERSION;
}

export interface DexResyncUpResult {
  /** "done" = re-upload ran and the marker was stamped; "deferred" = not authed
   *  or the endpoint is not deployed yet (retry next launch, marker unstamped);
   *  "skipped" = already done on this device. */
  outcome: "done" | "deferred" | "skipped";
  /** Entries pushed to the server this run. */
  posted: number;
}

/**
 * Run the one-time resync-up if it is owed and the user is authenticated.
 * Best-effort: any failure defers to the next launch WITHOUT stamping the
 * marker, so the restore is never lost to a transient error.
 */
export async function runDeferredDexResyncUp(
  apiFetch: (method: string, url: string, data?: unknown) => Promise<Response>,
  isAuthenticated: () => boolean,
): Promise<DexResyncUpResult> {
  if (!isDexResyncUpPending()) return { outcome: "skipped", posted: 0 };
  if (!isAuthenticated()) return { outcome: "deferred", posted: 0 };
  try {
    const res = await resyncLocalDexToServer(apiFetch);
    // Endpoint absent on a partial rollout: do NOT stamp — retry next launch.
    if (res.gap) return { outcome: "deferred", posted: 0 };
    writeResyncUpVersion(DEX_RESYNC_UP_VERSION);
    return { outcome: "done", posted: res.posted };
  } catch {
    // Network / auth blip: leave the marker unstamped so the next launch retries.
    return { outcome: "deferred", posted: 0 };
  }
}

/** Delay between resync-up retry attempts within a single launch. */
export const DEX_RESYNC_UP_RETRY_DELAY_MS = 2500;
/**
 * Max resync-up attempts per launch. 20 × 2.5s ≈ 50s, which comfortably covers
 * the native SecureStorage → in-memory token hydration that gates `isAuthenticated`.
 * After the cap a genuinely signed-out / offline device stops retrying until the
 * next launch (the marker is still unstamped, so nothing is lost).
 */
export const DEX_RESYNC_UP_MAX_ATTEMPTS = 20;

/**
 * Fire the resync-up after launch WITHOUT blocking startup, retrying until auth
 * is ready.
 *
 * WHY THE RETRY (this is the important bit)
 * -----------------------------------------
 * On native the session token is loaded ASYNCHRONOUSLY from SecureStorage after
 * launch (see AuthProvider / lib/tokenStorage), so `isAuthenticated()` is false
 * for the first beat of the app's life. The old one-shot fired via queueMicrotask
 * BEFORE that hydration finished, saw "not authed", deferred WITHOUT stamping —
 * and never ran again that session. Every launch lost the same race, so the
 * marker stayed unstamped forever and the dex never re-uploaded, even though the
 * sync path itself was perfectly healthy.
 *
 * The fix: on a "deferred" outcome (not-authed-yet, or a transient gap/error),
 * retry on a short timer until the token hydrates and the upload lands — or the
 * attempt cap is hit. "done"/"skipped" are terminal and stop the loop. The
 * upload is an idempotent upsert, so an extra attempt is always harmless.
 */
export function scheduleDeferredDexResyncUp(
  apiFetch: (method: string, url: string, data?: unknown) => Promise<Response>,
  isAuthenticated: () => boolean,
): void {
  let attempts = 0;
  const attempt = async (): Promise<void> => {
    attempts += 1;
    let outcome: DexResyncUpResult["outcome"] = "deferred";
    try {
      ({ outcome } = await runDeferredDexResyncUp(apiFetch, isAuthenticated));
    } catch {
      /* treat as deferred so we retry; the marker stays unstamped */
    }
    if (outcome === "deferred" && attempts < DEX_RESYNC_UP_MAX_ATTEMPTS) {
      setTimeout(() => {
        void attempt();
      }, DEX_RESYNC_UP_RETRY_DELAY_MS);
    }
  };
  const fire = () => {
    void attempt();
  };
  if (typeof queueMicrotask === "function") {
    queueMicrotask(fire);
  } else {
    void Promise.resolve().then(fire);
  }
}
